"use client";

import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface IDCellProps {
  id: string;
  className?: string;
}

/** A long provider/record id as a compact chip: hover to read it in full, click to copy. */
export const IDCell = ({ id, className }: IDCellProps) => {
  const [copied, setCopied] = useState(false);

  if (!id) {
    return <span className="text-sm text-muted-foreground">N/A</span>;
  }

  const shortId = id.length > 14 ? `${id.slice(0, 7)}…${id.slice(-4)}` : id;

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      toast.success("ID copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy ID");
    }
  };

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={copyToClipboard}
            aria-label={`Copy ID ${id}`}
            className={cn(
              "group inline-flex max-w-full items-center gap-1.5 rounded-full border border-border/60 bg-muted/40 py-1 pl-2.5 pr-2 font-mono text-xs tracking-tight text-muted-foreground transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              className
            )}
          >
            <span className="truncate">{shortId}</span>
            {copied ? (
              <Check className="size-3.5 shrink-0 text-green-600" />
            ) : (
              <Copy className="size-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[320px] break-all font-mono text-xs">
          {copied ? "Copied" : id}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
